import React, { useState, useCallback } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import QuickActions from './QuickActions';
import AccountSummary from './AccountSummary';
import EnhancedForexTable from './EnhancedForexTable';
import { AccountDetails } from './account';
import { ChangePasswordDetails } from './changePassword';
import { UserRoleManagement } from './userRole';
import { GroupModulePermissions } from './groupModulePermissions';
import { UserMaintenance } from './userMaintenance';
import { UpdateUserForm } from './userMaintenance/components';
import { AccountExecutiveMaintenance } from './accountExecutiveMaintenance';
import { TravelAgencyMaintenance } from './travelAgencyMaintenance';
import { TaxRateMaintenance } from './taxRateMaintenance';
import { ItineraryMaintenance } from './itineraryMaintenance';
import { AuditLogs } from './auditLogs';
import { PlanDownload } from './planDownload';
import { PlanUpload } from './planUpload';
import type { UserGroup, Permission } from '../../../types/userRole';
import type { User } from '../../../types/userMaintenance';
import type { PortalTheme } from '../../../shared/types/dashboard';
import '../../../styles/dashboard.css';

export type DashboardView = 'dashboard' | 'account' | 'change-password' | 'group-maintenance' | 'group-module-permissions' | 'user-maintenance' | 'user-edit' | 'account-executive-maintenance' | 'travel-agency-maintenance' | 'tax-rate-maintenance' | 'itinerary-maintenance' | 'audit-logs' | 'plan-download' | 'plan-upload';

interface DashboardProps {
  className?: string;
  theme?: PortalTheme;
}

const Dashboard: React.FC<DashboardProps> = ({ className = '', theme = 'admin' }) => {
  const { isAuthenticated } = useAuth();
  const [currentView, setCurrentView] = useState<DashboardView>('dashboard');
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [externalPermissionsHandler, setExternalPermissionsHandler] = useState<((permissions: Permission[]) => void) | null>(null);

  // All useCallback hooks must be defined before any early returns
  const handlePermissionsComplete = useCallback((permissions: Permission[]) => {
    console.log('Dashboard: handlePermissionsComplete called', {
      selectedGroup: selectedGroup?.name,
      isCreateMode,
      hasHandler: !!externalPermissionsHandler,
      permissionsCount: permissions.length
    });
    
    if (selectedGroup && isCreateMode && externalPermissionsHandler) {
      // For create mode, call the external handler to add the group with permissions
      console.log('Dashboard: Calling external permissions handler for new group:', selectedGroup.name, permissions);
      externalPermissionsHandler(permissions);
      
      // 🔥 FIX: Don't navigate here - let GroupModulePermissions call onBack()
      // The success modal will be shown in UserRoleManagement after navigation
      console.log('Dashboard: Permissions handler called, waiting for navigation from GroupModulePermissions');
      
    } else {
      // Handle edit mode permissions update
      console.log('Dashboard: Handling edit mode or fallback case');
      setSelectedGroup(null);
      setIsCreateMode(false);
      setCurrentView('group-maintenance');
    }
  }, [selectedGroup, isCreateMode, externalPermissionsHandler]);

  const handleSetPermissionsHandler = useCallback((handler: (permissions: Permission[]) => void) => {
    setExternalPermissionsHandler(() => handler);
  }, []);

  const handleSuccessModalComplete = useCallback(() => {
    console.log('Dashboard: Success modal completed, navigating back');
    setSelectedGroup(null);
    setIsCreateMode(false);
    setCurrentView('group-maintenance');
  }, []);

  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user);
    setCurrentView('user-edit');
  }, []);

  const handleBackToUserMaintenance = useCallback(() => {
    setSelectedUser(null);
    setCurrentView('user-maintenance');
  }, []);

  const handleSaveUser = useCallback((updatedUser: User) => {
    // In a real application, this would save to the backend
    console.log('Saving user:', updatedUser);
    // For now, just navigate back
    handleBackToUserMaintenance();
  }, [handleBackToUserMaintenance]);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return null; // This will be handled by route protection
  }

  const getHeaderTitle = () => {
    if (theme === 'travel-payment') {
      switch (currentView) {
        case 'account':
          return 'Account Management';
        case 'change-password':
          return 'Change Password';
        case 'audit-logs':
          return 'Recent Audit Activities';
        default:
          return 'Travel Portal Dashboard';
      }
    }

    // Admin theme titles
    switch (currentView) {
      case 'account':
        return 'Account Management';
      case 'change-password':
        return 'Change Password';
      case 'group-maintenance':
        return 'User Group Management';
      case 'group-module-permissions':
        return 'User Group Management';
      case 'user-maintenance':
        return 'User Maintenance';
      case 'user-edit':
        return 'User Maintenance';
      case 'account-executive-maintenance':
        return 'Account Executive Maintenance';
      case 'travel-agency-maintenance':
        return 'Travel Agency Maintenance';
      case 'tax-rate-maintenance':
        return 'Tax Rate Maintenance';
      case 'itinerary-maintenance':
        return 'Travel Management System';
      case 'audit-logs':
        return 'Recent Audit Activities';
      case 'plan-download':
        return 'Pacific Cross';
      case 'plan-upload':
        return 'Pacific Cross';
      default:
        return 'Admin Portal Dashboard';
    }
  };

  const handleConfigurePermissions = (group: UserGroup) => {
    setSelectedGroup(group);
    setIsCreateMode(false); // Explicitly set edit mode
    setCurrentView('group-module-permissions');
  };

  const handleCreateGroupPermissions = (group: UserGroup) => {
    setSelectedGroup(group);
    setIsCreateMode(true); // Set create mode for new groups
    setCurrentView('group-module-permissions');
  };

  const handleBackToGroupManagement = () => {
    setSelectedGroup(null);
    setIsCreateMode(false);
    setCurrentView('group-maintenance');
  };


  const renderMainContent = () => {
    // Common views for both themes
    if (currentView === 'account') {
      return <AccountDetails />;
    }
    
    if (currentView === 'change-password') {
      return <ChangePasswordDetails />;
    }
    
    if (currentView === 'audit-logs') {
      return <AuditLogs />;
    }

    // Admin-only views
    if (theme === 'admin') {
      if (currentView === 'group-maintenance') {
        return (
          <UserRoleManagement
            onConfigurePermissions={handleConfigurePermissions}
            onCreateGroupPermissions={handleCreateGroupPermissions}
            onPermissionsComplete={handleSetPermissionsHandler}
            onSuccessModalComplete={handleSuccessModalComplete}
          />
        );
      }
      
      if (currentView === 'group-module-permissions') {
        return (
          <GroupModulePermissions
            selectedGroup={selectedGroup}
            onBack={handleBackToGroupManagement}
            mode={isCreateMode ? 'create' : 'edit'}
            onPermissionsComplete={isCreateMode ? handlePermissionsComplete : undefined}
          />
        );
      }
      
      if (currentView === 'user-maintenance') {
        return <UserMaintenance onEditUser={handleEditUser} />;
      }
      
      if (currentView === 'user-edit') {
        return (
          <UpdateUserForm
            user={selectedUser!}
            onSave={handleSaveUser}
            onCancel={handleBackToUserMaintenance}
          />
        );
      }
      
      if (currentView === 'account-executive-maintenance') {
        return <AccountExecutiveMaintenance />;
      }
      
      if (currentView === 'travel-agency-maintenance') {
        return <TravelAgencyMaintenance />;
      }
      
      if (currentView === 'tax-rate-maintenance') {
        return <TaxRateMaintenance />;
      }
      
      if (currentView === 'itinerary-maintenance') {
        return <ItineraryMaintenance />;
      }
      
      if (currentView === 'plan-download') {
        return <PlanDownload />;
      }
      
      if (currentView === 'plan-upload') {
        return <PlanUpload />;
      }
    }
    
    // Default dashboard view (works for both themes)
    return (
      <>
        <div className="dashboard-top-section">
          <QuickActions
            onNewTransaction={handleNewTransaction}
            onViewReports={handleViewReports}
          />
          
          <AccountSummary />
        </div>
        
        <EnhancedForexTable />
      </>
    );
  };

  const handleNewTransaction = () => {
    // Interactive prototype behavior
    alert('New Transaction Modal\n\nThis would open a transaction form with:\n- Transaction type selection\n- Customer details\n- Payment information\n- Confirmation steps');
  };

  const handleViewReports = () => {
    // Interactive prototype behavior
    alert('View Reports Modal\n\nThis would show:\n- Transaction reports\n- Financial summaries\n- Export options\n- Date range filters');
  };

  return (
    <div className={`dashboard-container ${theme === 'travel-payment' ? 'travel-payment-theme' : ''} ${className}`}>
      <DashboardSidebar
        currentView={currentView}
        onViewChange={setCurrentView}
        theme={theme}
      />
      
      <main className="dashboard-main">
        <DashboardHeader title={getHeaderTitle()} />
        {renderMainContent()}
      </main>
    </div>
  );
};

export default Dashboard;